import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Modal, Input, Spin, Empty, InputRef } from "antd";
import {
  SearchOutlined,
  LoadingOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { visitNotesController, timeProvider } from "../../../AppController";
import { useAppSelector } from "../../../hooks/redux";
import styles from "./styles.module.scss";

interface IVisitNoteSearchResult {
  noteId: number;
  patientId: number;
  patientName: string;
  visitDate: string;
  visitType: string;
  noteContent: string;
  docType: "np_visit" | "at_risk_review" | "third_party_np_visit";
}

interface IVisitNotesSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelectNote: (note: IVisitNoteSearchResult) => void;
}

const VisitNotesSearchModal = ({
  open,
  onClose,
  onSelectNote,
}: IVisitNotesSearchModalProps) => {
  const { userClinic } = useAppSelector((state) => ({
    userClinic: state.clinic.userClinic,
  }));

  const [searchQuery, setSearchQuery] = useState("");
  const [allNotes, setAllNotes] = useState<IVisitNoteSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<InputRef>(null);

  // Debug flag - set to false to disable logs
  const DEBUG = true;
  const log = useCallback(
    (...args: any[]) => {
      // eslint-disable-next-line no-console
      if (DEBUG) console.log(...args);
    },
    [DEBUG]
  );

  // Log component lifecycle
  useEffect(() => {
    log("🔧 VisitNotesSearchModal mounted");
    return () => {
      log("💀 VisitNotesSearchModal unmounted");
    };
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    log("🚪 Modal open state changed:", open);
    if (open) {
      // Clear everything when opening
      log("🔄 Resetting state on modal open");
      setSearchQuery("");
      setAllNotes([]);
      setIsLoading(false);
    }
  }, [open, log]);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (open && inputRef.current) {
      log("🎯 Auto-focusing input");
      // Small delay to ensure input is mounted
      const timer = setTimeout(() => {
        inputRef.current?.focus();
        log("✅ Input focused");
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, log]);

  // Fetch all visit notes when modal opens
  useEffect(() => {
    if (open && userClinic?.id) {
      fetchAllNotes();
    }
  }, [open, userClinic?.id]);

  const fetchAllNotes = async () => {
    if (!userClinic?.id) return;

    log("📥 Fetching all visit notes for clinic:", userClinic.id);
    setIsLoading(true);
    try {
      const result = await visitNotesController.getAllVisitNotes(userClinic.id);

      if (result.success && result.data?.patients) {
        const notes: IVisitNoteSearchResult[] = [];

        result.data.patients.forEach((patient: any) => {
          if (patient.notes && Array.isArray(patient.notes)) {
            patient.notes.forEach((note: any) => {
              // Only include third_party_np_visit notes
              if (note.doc_type === "third_party_np_visit") {
                // Parse note content
                let noteContent = "";
                try {
                  const parsedNotes =
                    typeof note.notes === "string"
                      ? JSON.parse(note.notes)
                      : note.notes;
                  noteContent = parsedNotes?.historyOfPresentIllness || "";
                } catch (error) {
                  console.error("Error parsing notes:", error);
                }

                notes.push({
                  noteId: note.note_id,
                  patientId: patient.patient_id,
                  patientName:
                    `${patient.first_name || ""} ${
                      patient.last_name || ""
                    }`.trim() ||
                    patient.name ||
                    "Unknown",
                  visitDate: note.created_at || "",
                  visitType: "Third Party NP Visit",
                  noteContent,
                  docType: note.doc_type,
                });
              }
            });
          }
        });

        // Sort by date, newest first
        notes.sort((a, b) => {
          const dateA = timeProvider.dateLibrary(a.visitDate);
          const dateB = timeProvider.dateLibrary(b.visitDate);
          return (dateB?.valueOf() || 0) - (dateA?.valueOf() || 0);
        });

        log("✅ Fetched notes:", {
          count: notes.length,
          patients: result.data.patients.length,
        });
        setAllNotes(notes);
      }
    } catch (error) {
      console.error("❌ Error fetching visit notes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter notes based on search query
  const filteredNotes = useMemo(() => {
    log("🔍 Filter triggered:", {
      searchQuery,
      trimmed: searchQuery.trim(),
      allNotesCount: allNotes.length,
    });

    if (!searchQuery.trim()) {
      log("✅ No search query, returning all notes");
      return allNotes;
    }

    const query = searchQuery.toLowerCase().trim();
    log("🔎 Searching for:", query);

    const filtered = allNotes.filter((note) => {
      const patientNameMatch = note.patientName.toLowerCase().includes(query);
      const noteContentMatch = note.noteContent.toLowerCase().includes(query);
      const visitTypeMatch = note.visitType.toLowerCase().includes(query);

      const formattedDate = timeProvider
        .convertIntoLocalDateTime(note.visitDate)
        ?.format("MM/DD/YYYY");
      const dateMatch = formattedDate?.includes(query);

      const isMatch =
        patientNameMatch || noteContentMatch || visitTypeMatch || dateMatch;

      if (isMatch) {
        log("✅ Match found:", {
          patientName: note.patientName,
          noteId: note.noteId,
          patientNameMatch,
          noteContentMatch,
          visitTypeMatch,
          dateMatch,
        });
      }

      return isMatch;
    });

    log("📊 Filter results:", {
      filteredCount: filtered.length,
      totalCount: allNotes.length,
      firstFewNotes: filtered.slice(0, 3).map((n) => ({
        id: n.noteId,
        name: n.patientName,
      })),
    });

    return filtered;
  }, [allNotes, searchQuery, log]);

  // Log whenever filteredNotes changes
  useEffect(() => {
    log("🔄 filteredNotes updated:", {
      count: filteredNotes.length,
      searchQuery,
      noteIds: filteredNotes.map((n) => n.noteId),
      hasDuplicateIds:
        filteredNotes.length !==
        new Set(filteredNotes.map((n) => n.noteId)).size,
    });
  }, [filteredNotes, searchQuery, log]);

  const handleSelectNote = (note: IVisitNoteSearchResult) => {
    onSelectNote(note);
    handleClose();
  };

  const handleClose = () => {
    setSearchQuery("");
    onClose();
  };

  // Highlight matching text - only highlight if text contains the query
  const highlightText = (text: string, query: string) => {
    log("🎨 Highlight called:", { text: text.substring(0, 50), query });

    if (!query.trim()) return text;

    // Check if text actually contains the query (case-insensitive)
    if (!text.toLowerCase().includes(query.toLowerCase())) {
      log("⏭️  Skipping highlight - text doesn't contain query");
      return text;
    }

    // Escape special regex characters in the query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    log("🔧 Escaped query:", escapedQuery);

    try {
      const parts = text.split(new RegExp(`(${escapedQuery})`, "gi"));
      log("✂️ Split parts:", {
        partsCount: parts.length,
        parts: parts.slice(0, 5),
      });

      return (
        <span>
          {parts.map((part, index) =>
            part.toLowerCase() === query.toLowerCase() ? (
              <mark key={index} className={styles.highlight}>
                {part}
              </mark>
            ) : (
              part
            )
          )}
        </span>
      );
    } catch {
      console.error("❌ Regex failed for query:", query);
      // If regex fails, return original text
      return text;
    }
  };

  // Truncate note content for preview
  const getTruncatedContent = (content: string, maxLength: number = 150) => {
    // Remove HTML tags
    const plainText = content.replace(/<[^>]*>/g, "");
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + "...";
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={742}
      className={styles.searchModal}
      closeIcon={<CloseOutlined className={styles.closeIcon} />}
      destroyOnClose={true}
    >
      <div className={styles.searchContainer}>
        <Input
          placeholder="Search by patient name, note content, or date..."
          prefix={<SearchOutlined className={styles.searchIcon} />}
          value={searchQuery}
          onChange={(e) => {
            const newValue = e.target.value;
            log("⌨️  Input onChange:", {
              oldValue: searchQuery,
              newValue,
              length: newValue.length,
            });
            setSearchQuery(newValue);
          }}
          className={styles.searchInput}
          ref={inputRef}
        />
      </div>
      <div
        className={styles.notesList}
        key={`notes-list-${searchQuery}-${filteredNotes.length}`}
      >
        {(() => {
          if (isLoading) {
            return (
              <Spin
                spinning={isLoading}
                className={styles.loading}
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 48, color: "#161646" }}
                    spin
                  />
                }
              />
            );
          }

          if (filteredNotes.length === 0) {
            return (
              <div className={styles.emptyState}>
                <Empty
                  description={
                    searchQuery.trim()
                      ? "No matching visit notes found"
                      : "No visit notes available"
                  }
                />
              </div>
            );
          }

          log("📋 Rendering notes list", {
            count: filteredNotes.length,
            searchQuery,
            noteIds: filteredNotes.map((n) => n.noteId).slice(0, 5),
          });

          return filteredNotes.map((note) => (
            <div
              key={`${note.noteId}-${note.patientId}-${note.visitDate}`}
              className={styles.noteItem}
              onClick={() => handleSelectNote(note)}
            >
              <div className={styles.noteInfo}>
                <div className={styles.noteHeader}>
                  <span className={styles.patientName}>
                    {searchQuery.trim()
                      ? highlightText(note.patientName, searchQuery)
                      : note.patientName}
                  </span>
                  <span className={styles.visitDate}>
                    {timeProvider
                      .convertIntoLocalDateTime(note.visitDate)
                      ?.format("MM/DD/YYYY hh:mm a")}
                  </span>
                </div>
                <div className={styles.visitType}>{note.visitType}</div>
                {note.noteContent && (
                  <div className={styles.notePreview}>
                    {searchQuery.trim()
                      ? highlightText(
                          getTruncatedContent(note.noteContent),
                          searchQuery
                        )
                      : getTruncatedContent(note.noteContent)}
                  </div>
                )}
              </div>
            </div>
          ));
        })()}
      </div>
    </Modal>
  );
};

export default VisitNotesSearchModal;
